import type { AbstractPowerSyncDatabase, PowerSyncBackendConnector } from '@powersync/web'
import { createClient } from '@/utils/supabase/client'
import { trackEvent } from '@/lib/posthog'
import { JSONB_COLUMNS } from './schema'
import {
  clearRetryWatch,
  deadLetterTransaction,
  hasServerVerdict,
  isFatalPgError,
  noteRetryFailure,
  retryBudgetExceeded,
  type PgErrorLike,
} from './dead-letters'

const supabase = createClient()

// iOS WKWebView can hang a single supabase-js POST forever, wedging the
// whole batch (batch.complete runs only after every op). 30s cap forces the
// batch to retry — upserts are idempotent. Well above PostgREST RTT.
const CRUD_OP_TIMEOUT_MS = 30_000

// getSession() reads from local storage (instant) but triggers a token refresh
// — a network call — when the access token is near expiry. On iOS WKWebView
// that refresh can hang forever offline (same fetch-wedge as the CRUD ops),
// which would stall PowerSync's connect/reconnect loop indefinitely. 8s is well
// above a real PostgREST/auth round-trip even on slow site 4G, so a legit
// refresh still completes; a wedged one bails and lets PowerSync retry on its
// own backoff. (The underlying refresh keeps running in the background, so the
// next retry usually reads the freshly-refreshed token straight from storage.)
const CREDENTIALS_TIMEOUT_MS = 8_000

class CrudOpTimeoutError extends Error {
  constructor(table: string, op: string, id: string) {
    super(`crud_op_timeout: ${op} ${table} ${id}`)
    this.name = 'CrudOpTimeoutError'
  }
}

async function withTimeout<T>(
  label: string,
  thenable: PromiseLike<T>,
  ms: number = CRUD_OP_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms)
  })
  try {
    return await Promise.race([Promise.resolve(thenable), timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// JSONB column registry lives in schema.ts next to the table defs. On upload
// we must JSON.parse so PostgREST receives a JSON value (object/array), not a
// string literal that JSONB stores as `type "string"` and every server reader
// crashes on. A CI test asserts the registry covers every JSONB column in PG.

// Columns the server owns — client rows always hold NULL, so including them in
// an upsert/update overwrites the server's value. Narrow per-column list;
// broader server-authoritative audit is deferred to Phase 8.
const STRIP_ON_UPLOAD_BY_TABLE: Record<string, ReadonlyArray<string>> = {
  // Written by /api/uploads/[analysisId] after the upload-image intent runs
  // (Storage upload + sharp-decoded dimensions). A racing PowerSync flush
  // would replay client NULLs and wipe the server values.
  analyses: ['imageUrl', 'imageWidth', 'imageHeight'],
}

function transformOpData(table: string, opData: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!opData) return {}
  const out: Record<string, unknown> = { ...opData }
  const jsonbCols = JSONB_COLUMNS[table]
  if (jsonbCols) {
    for (const col of jsonbCols) {
      const v = out[col]
      if (typeof v === 'string' && v.length > 0) {
        try { out[col] = JSON.parse(v) } catch { /* leave as string; server will 400 and surface the bug */ }
      }
    }
  }
  const stripCols = STRIP_ON_UPLOAD_BY_TABLE[table]
  if (stripCols) {
    for (const col of stripCols) delete out[col]
  }
  return out
}

export class SupabasePowerSyncConnector implements PowerSyncBackendConnector {
  async fetchCredentials() {
    console.log('[PS-DIAG] fetchCredentials called')
    // Race the refresh-capable getSession() against a timeout so a hung offline
    // refresh can't wedge the connect loop. Throwing on timeout is the right
    // signal — PowerSync treats a credential failure as transient and retries.
    const { data: { session } } = await withTimeout(
      'fetch_credentials_timeout',
      supabase.auth.getSession(),
      CREDENTIALS_TIMEOUT_MS,
    )
    if (!session) {
      console.error('[PS-DIAG] No Supabase session in client')
      throw new Error('No Supabase session — user must sign in first')
    }
    const endpoint = process.env.VITE_POWERSYNC_URL
    console.log('[PS-DIAG] credentials ok', {
      endpoint,
      userId: session.user.id,
      tokenLen: session.access_token.length,
      tokenExp: new Date((session.expires_at ?? 0) * 1000).toISOString(),
    })
    return {
      endpoint: endpoint!,
      token: session.access_token,
      userID: session.user.id,
    }
  }

  async uploadData(db: AbstractPowerSyncDatabase) {
    // One local write transaction at a time (PowerSync's documented pattern):
    // a poisoned transaction can be discarded as a unit without dragging
    // unrelated ops down with it. See dead-letters.ts for the incident notes.
    for (;;) {
      const tx = await db.getNextCrudTransaction()
      if (!tx) return
      const startedAt = Date.now()
      const tables: Record<string, number> = {}
      let slowOpMs = 0
      let slowOpTable = ''
      try {
        for (const op of tx.crud) {
          const data = transformOpData(op.table, op.opData as Record<string, unknown> | null | undefined)
          const label = new CrudOpTimeoutError(op.table, op.op, String(op.id)).message
          tables[op.table] = (tables[op.table] ?? 0) + 1
          const opStart = Date.now()
          if (op.op === 'PUT') {
            const row = { id: op.id, ...data }
            const { error } = await withTimeout(label, supabase.from(op.table).upsert(row))
            if (error) throw error
          } else if (op.op === 'PATCH') {
            const { error } = await withTimeout(label, supabase.from(op.table).update(data).eq('id', op.id))
            if (error) throw error
          } else if (op.op === 'DELETE') {
            const { error } = await withTimeout(label, supabase.from(op.table).delete().eq('id', op.id))
            if (error) throw error
          }
          const opMs = Date.now() - opStart
          if (opMs > slowOpMs) { slowOpMs = opMs; slowOpTable = `${op.op}:${op.table}` }
        }
        await tx.complete()
        clearRetryWatch()
        trackEvent('powersync_upload', {
          durationMs: Date.now() - startedAt,
          opCount: tx.crud.length,
          tables,
          slowOpMs,
          slowOpTable,
        })
      } catch (err) {
        // Poisoned-op handling. Fatal PG codes discard immediately; other
        // server-rejected errors discard after the retry budget; timeouts and
        // network failures (no server verdict) retry forever.
        const txKey = tx.crud[0]?.clientId ?? -1
        let discard = isFatalPgError(err)
        if (!discard && hasServerVerdict(err)) {
          noteRetryFailure(txKey)
          discard = retryBudgetExceeded(txKey)
        }
        if (discard) {
          // Parking is diagnostics; the discard is the incident fix. A failed park
          // (SQLite locked, disk full) must not keep the poisoned tx wedging the
          // queue — worst case we lose the local trace but keep the PostHog event.
          try {
            await deadLetterTransaction(db, tx.crud, err as PgErrorLike)
          } catch (parkErr) {
            console.error('[PowerSync] dead-letter parking failed', parkErr)
          }
          await tx.complete()
          clearRetryWatch()
          trackEvent('powersync_upload_discarded', {
            opCount: tx.crud.length,
            tables,
            errorCode: (err as PgErrorLike)?.code ?? '',
            error: err instanceof Error ? err.message : String((err as PgErrorLike)?.message ?? err),
          })
          continue
        }
        trackEvent('powersync_upload_failed', {
          durationMs: Date.now() - startedAt,
          opCount: tx.crud.length,
          tables,
          error: err instanceof Error ? err.message : String(err),
        })
        throw err
      }
    }
  }
}
